import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import {
  AuthProvider,
  ThemeProvider,
  CurrencyProvider,
  FeatureFlagProvider,
  AchievementsProvider,
  CleviaGuideProvider,
  CleviaFeedbackProvider,
} from '@/contexts';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { FeatureRoute } from '@/components/FeatureRoute';
import { AdminProtectedRoute } from '@/admin/components/AdminProtectedRoute';
import HomePage from '@/pages/HomePage';
import DashboardPage from '@/pages/DashboardPage';
import LoginPage from '@/pages/LoginPage';
import NewRegisterPage from '@/pages/NewRegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import Method503020Page from '@/pages/Method503020Page';
import PricingPage from '@/pages/PricingPage';
import BlogPage from '@/pages/BlogPage';
import PressPage from '@/pages/PressPage';
import FoundersPage from '@/pages/FoundersPage';
import SettingsPage from '@/pages/SettingsPage';
import TransactionsPage from '@/pages/TransactionsPage';
import AccountsPage from '@/pages/AccountsPage';
import AccountDetailsPage from '@/pages/AccountDetailsPage';
import GoalsPage from '@/pages/GoalsPage';
import BudgetsPage from '@/pages/BudgetsPage';
import AiAssistantPage from '@/pages/AiAssistantPage';
import CoachPage from '@/pages/CoachPage';
import AddBankAccountPage from '@/pages/AddBankAccountPage';
import PowensCallbackPage from '@/pages/PowensCallbackPage';
import CalendarPage from '@/pages/CalendarPage';
import AdminChoicePage from '@/admin/pages/AdminChoicePage';
import GlobalOverviewPage from '@/admin/pages/GlobalOverviewPage';
import UserGrowthPage from '@/admin/pages/UserGrowthPage';
import ProductUsagePage from '@/admin/pages/ProductUsagePage';
import EngagementPage from '@/admin/pages/EngagementPage';
import ReferralPage from '@/admin/pages/ReferralPage';
import FinancialPage from '@/admin/pages/FinancialPage';
import StatusPage from '@/admin/pages/StatusPage';
import AdminTeamPage from '@/admin/pages/AdminTeamPage';
import FeatureFlagsPage from '@/admin/pages/FeatureFlagsPage';
import CmsPagesPage from '@/admin/pages/CmsPagesPage';
import CmsEditorPage from '@/admin/pages/CmsEditorPage';
import { MaintenanceGate } from '@/components/MaintenanceGate';
import { CookieConsent } from '@/components/CookieConsent';
import { usePageView } from '@/hooks/usePageView';
import CmsPageView from '@/pages/CmsPage';
import { CleviaGettingStartedModal } from '@/components/CleviaGettingStartedModal';
import HelpCenterPage from './pages/HelpCenterPage';

function PageViewTracker() {
  usePageView();
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ThemeProvider>
      <CurrencyProvider>
        <ConfirmProvider>
          <AuthProvider>
            <FeatureFlagProvider>
              <AchievementsProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <CleviaGuideProvider>
                    <CleviaFeedbackProvider>
                      <MaintenanceGate>
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="/blog" element={<BlogPage />} />
                          <Route path="/press" element={<PressPage />} />
                          <Route path="/founders" element={<FoundersPage />} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register" element={<NewRegisterPage />} />
                          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                          <Route path="/reset-password" element={<ResetPasswordPage />} />
                          <Route path="/help" element={<HelpCenterPage />} />
                          <Route path="/help/:categorySlug" element={<HelpCenterPage />} />
                          <Route path="/method/50-30-20" element={<Method503020Page />} />
                          <Route
                            path="/dashboard"
                            element={
                              <ProtectedRoute>
                                <DashboardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="settings">
                                  <SettingsPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/transactions"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="transactions">
                                  <TransactionsPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/accounts"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="accounts">
                                  <AccountsPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/accounts/add-bank"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="accounts">
                                  <AddBankAccountPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/accounts/powens/callback"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="accounts">
                                  <PowensCallbackPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/accounts/:id/edit"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="accounts">
                                  <AddBankAccountPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/accounts/:id"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="accounts">
                                  <AccountDetailsPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/goals"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="goals">
                                  <GoalsPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/budgets"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="budgets">
                                  <BudgetsPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/coach"
                            element={
                              <ProtectedRoute>
                                <CoachPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-assistant"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="ai_chat">
                                  <AiAssistantPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/calendar"
                            element={
                              <ProtectedRoute>
                                <FeatureRoute featureKey="calendar">
                                  <CalendarPage />
                                </FeatureRoute>
                              </ProtectedRoute>
                            }
                          />
                          {/* Admin routes */}
                          <Route
                            path="/admin/choice"
                            element={
                              <AdminProtectedRoute>
                                <AdminChoicePage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              <AdminProtectedRoute>
                                <GlobalOverviewPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/users"
                            element={
                              <AdminProtectedRoute>
                                <UserGrowthPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/usage"
                            element={
                              <AdminProtectedRoute>
                                <ProductUsagePage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/engagement"
                            element={
                              <AdminProtectedRoute>
                                <EngagementPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/referral"
                            element={
                              <AdminProtectedRoute>
                                <ReferralPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/financial"
                            element={
                              <AdminProtectedRoute>
                                <FinancialPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/status"
                            element={
                              <AdminProtectedRoute>
                                <StatusPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/team"
                            element={
                              <AdminProtectedRoute>
                                <AdminTeamPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/features"
                            element={
                              <AdminProtectedRoute>
                                <FeatureFlagsPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/pages"
                            element={
                              <AdminProtectedRoute>
                                <CmsPagesPage />
                              </AdminProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin/pages/:id/edit"
                            element={
                              <AdminProtectedRoute>
                                <CmsEditorPage />
                              </AdminProtectedRoute>
                            }
                          />
                          {/* Public CMS pages */}
                          <Route path="/p/*" element={<CmsPageView />} />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <CleviaGettingStartedModal />
                      </MaintenanceGate>
                    </CleviaFeedbackProvider>
                  </CleviaGuideProvider>
                  <CookieConsent />
                  <PageViewTracker />
                  <Toaster />
                </BrowserRouter>
              </AchievementsProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        </ConfirmProvider>
      </CurrencyProvider>
    </ThemeProvider>
  );
}

export default App;
